import {APIRegistry} from '@spothero/utils/api';
import {
    formatTransientSearchResponse,
    formatTransientSpotDetailsResponse,
} from './transient/transient-utils';
import {formatMonthlySearchResponse} from './monthly/monthly-utils';
import {
    formatAirportSearchResponse,
    formatAirportSpotDetailsResponse,
} from './airport/airport-utils';

/**
 * Searches for available transient parking.
 *
 * @function getTransientFacilities
 * @see {@link https://api.spothero.com/v2/docs#operation/searchTransientParking|Documentation}
 * @param {object} params - The parameters to pass to the endpoint.
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @example
 * getTransientFacilities({lat: 41.8781, lon: -87.6298});
 * @returns {Array} The available transient facilities, with data transformation applied.
 */
export function getTransientFacilities(params, formatOptions) {
    const api = APIRegistry.get('craigSearch');

    /* eslint-disable camelcase */
    return api
        .get('search/transient', {oversize: false, page_size: 25, ...params})
        .then(({data: {results, '@next': loadMoreSpotsUrl}}) => ({
            loadMoreSpotsUrl,
            results: formatTransientSearchResponse(results, formatOptions),
        }));
    /* eslint-enable camelcase */
}

/**
 * Load more available transient parking after initial search for transient parking.
 * Uses the @next URL returned by search api to load next set of spots.
 *
 * @function getTransientFacilitiesLoadMoreSpots
 * @see {@link https://api.spothero.com/v2/docs#operation/searchTransientParking|Documentation}
 * @param {object} params - The parameters to pass to the endpoint.
 * @param {string} params.loadMoreSpotsUrl - The @next URL returned by search api to load next set of spots.
 * @param {object} [formatOptions] - Additional options that don't get passed to the endpoint but are used for data transformation
 * @example
 * getTransientFacilitiesLoadMoreSpots({loadMoreSpotsUrl: 'https://api.staging.spothero.com/v2/search/transie…w_unavailable=true&starts=2021-07-23T08%3A30%3A00'});
 * @returns {Array} The available transient facilities, with data transformation applied.
 */
export function getTransientFacilitiesLoadMoreSpots(
    {loadMoreSpotsUrl},
    formatOptions
) {
    const api = APIRegistry.get('craigSearch');

    /* eslint-disable camelcase */
    return api
        .get(loadMoreSpotsUrl)
        .then(({data: {results, '@next': nextLoadMoreSpotsUrl}}) => ({
            loadMoreSpotsUrl: nextLoadMoreSpotsUrl,
            results: formatTransientSearchResponse(results, formatOptions),
        }));
    /* eslint-enable camelcase */
}

/**
 * Retrieves data for a single transient facility
 *
 * @function getTransientFacility
 * @see {@link https://api.spothero.com/v2/docs#operation/searchTransientFacilityParking|Documentation}
 * @param {string} facilityId - The id which uniquely identifies a facility
 * @param {object} params - The parameters to pass to the endpoint.
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @example
 * getTransientFacility(123, {lat: 41.8781, lon: -87.6298});
 * @returns {object} The transient facility, with data transformation applied.
 */
export function getTransientFacility(facilityId, params, formatOptions) {
    const api = APIRegistry.get('craigSearch');

    /* eslint-disable camelcase */
    return api
        .get(`search/transient/${facilityId}`, {
            oversize: false,
            show_unavailable: true,
            ...params,
        })
        .then(({data: {result}}) =>
            formatTransientSpotDetailsResponse(result, formatOptions)
        );
    /* eslint-enable camelcase */
}

/**
 * Searches for available monthly parking.
 *
 * @function getMonthlyFacilities
 * @see {@link https://api.spothero.com/v2/docs#operation/searchMonthlyParking|Documentation}
 * @param {object} params - The parameters to pass to the endpoint.
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @example
 * getMonthlyFacilities({lat: 41.8781, lon: -87.6298});
 * @returns {Array} The available monthly facilities, with data transformation applied.
 */
export function getMonthlyFacilities(params, formatOptions) {
    const api = APIRegistry.get('craigSearch');

    /* eslint-disable camelcase */
    return api
        .get('search/monthly', {oversize: false, page_size: 25, ...params})
        .then(({data: {results, '@next': loadMoreSpotsUrl}}) => ({
            loadMoreSpotsUrl,
            results: formatMonthlySearchResponse(results, formatOptions),
        }));
    /* eslint-enable camelcase */
}

/**
 * Load more available monthly parking after initial search.
 * Uses the @next URL returned by search api to load next set of spots.
 *
 * @function getMonthlyFacilitiesLoadMoreSpots
 * @see {@link https://api.spothero.com/v2/docs#operation/searchMonthlyParking|Documentation}
 * @param {object} params - The parameters to pass to the endpoint.
 * @param {string} params.loadMoreSpotsUrl - The @next URL returned by search api to load next set of spots.
 * @param {object} [formatOptions] - Additional options that don't get passed to the endpoint but are used for data transformation
 * @example
 * getMonthlyFacilitiesLoadMoreSpots({loadMoreSpotsUrl: 'https://api.staging.spothero.com/v2/search/...'});
 * @returns {Array} The available monthly facilities, with data transformation applied.
 */
export function getMonthlyFacilitiesLoadMoreSpots(
    {loadMoreSpotsUrl},
    formatOptions
) {
    const api = APIRegistry.get('craigSearch');

    /* eslint-disable camelcase */
    return api
        .get(loadMoreSpotsUrl)
        .then(({data: {results, '@next': nextLoadMoreSpotsUrl}}) => ({
            loadMoreSpotsUrl: nextLoadMoreSpotsUrl,
            results: formatMonthlySearchResponse(results, formatOptions),
        }));
    /* eslint-enable camelcase */
}

/**
 * Searches for available airport parking.
 *
 * @function getAirportFacilities
 * @see {@link https://api.spothero.com/v2/docs#operation/searchAirportParking|Documentation}
 * @param {object} params - The parameters to pass to the endpoint.
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @example
 * getAirportFacilities({iata: 'ORD});
 * @returns {Array} The available airport facilities, with data transformation applied.
 */
export function getAirportFacilities(params, formatOptions) {
    const api = APIRegistry.get('craigSearch');

    /* eslint-disable camelcase */
    return api
        .get('search/airport', {oversize: false, ...params})
        .then(({data: {results}}) =>
            formatAirportSearchResponse(results, formatOptions)
        );
    /* eslint-enable camelcase */
}

/**
 * Retrieves data for a single airport facility
 *
 * @function getAirportFacility
 * @see {@link https://api.spothero.com/v2/docs#operation/searchAirportFacilityParking|Documentation}
 * @param {string} facilityId - The id which uniquely identifies a facility
 * @param {object} params - The parameters to pass to the endpoint.
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @example
 * getAirportFacility(123, {starts: '2025-10-10T10:00:00.000Z', ends: '2025-10-10T12:00:00.000Z'});
 * @returns {object} The airport facility, with data transformation applied.
 */
export function getAirportFacility(facilityId, params, formatOptions) {
    const api = APIRegistry.get('craigSearch');

    /* eslint-disable camelcase */
    return api
        .get(`search/airport/${facilityId}`, {
            oversize: false,
            show_unavailable: true,
            ...params,
        })
        .then(({data: {result}}) =>
            formatAirportSpotDetailsResponse(result, formatOptions)
        );
    /* eslint-enable camelcase */
}

// TODO: when single facility API endpoint story comes up
// export function getMonthlyFacility(id) {
//     const api = APIRegistry.get('craig');
// }
